import React from 'react';
import { Link } from 'react-router';
import { TermsSection } from './shared';

export default function AcceptableUsePolicyPage() {
  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <h1 style={{ color: '#e2e8f0', fontSize: '1.8rem', fontWeight: 700, marginBottom: 8 }}>
        Acceptable Use Policy
      </h1>
      <p style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
        Last updated: March 27, 2026
      </p>
      <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.7, marginBottom: 32 }}>
        This Acceptable Use Policy ("AUP") supplements the
        CrimsonScan <Link to="/docs/terms" style={{ color: '#38bdf8', textDecoration: 'none' }}>Terms of Service</Link> and
        governs your use of the Spectra platform. Capitalized terms not defined herein have the meanings
        given in the Terms of Service.
      </p>

      <TermsSection num="1" title="Purpose">
        This AUP exists to protect the integrity, security, and availability of the Spectra platform
        for all users. It expands on the acceptable use provisions in Section 6 of the Terms of Service
        and provides detailed guidance on permitted and prohibited activities. All users of the Service
        are expected to comply with this policy.
      </TermsSection>

      <TermsSection num="2" title="Permitted Use">
        Spectra is designed for legitimate materials science research, discovery, and analysis.
        Permitted uses include:
        <ul style={{ paddingLeft: 20, margin: '8px 0 0 0' }}>
          <li>Computational materials discovery and candidate generation</li>
          <li>Structural, electronic, and thermodynamic analysis of generated or user-provided materials</li>
          <li>Density functional theory (DFT) and surrogate model calculations for materials characterization</li>
          <li>Report generation for scientific documentation, economic analysis, and intellectual property assessment</li>
          <li>Comparison of candidate materials against known databases (e.g. Materials Project)</li>
          <li>Annotation and collaborative review of candidate materials within your organization</li>
          <li>Educational and academic research purposes</li>
        </ul>
      </TermsSection>

      <TermsSection num="3" title="Prohibited Activities">
        You agree not to use the Service to:
        <ul style={{ paddingLeft: 20, margin: '8px 0 0 0' }}>
          <li>Reverse-engineer, decompile, disassemble, or otherwise attempt to derive the source code,
            algorithms, or data models of the platform</li>
          <li>Develop, train, or improve competing materials discovery or generative modeling products
            using insights, outputs, or data obtained from the Service</li>
          <li>Circumvent or attempt to circumvent access controls, authentication mechanisms, rate limits,
            usage quotas, or other security measures</li>
          <li>Upload malicious code, malware, or content designed to exploit vulnerabilities in the
            platform infrastructure</li>
          <li>Generate, design, or optimize materials intended for use in weapons, explosives, chemical
            or biological agents, or other applications intended to cause harm to human health or
            the environment</li>
          <li>Use the Service for any purpose that violates applicable local, state, national, or
            international law or regulation</li>
          <li>Impersonate another user, organization, or entity</li>
          <li>Share, resell, sublicense, or redistribute access to the Service without prior
            written authorization from CrimsonScan</li>
          <li>Systematically scrape, crawl, or extract data from the platform through automated means
            outside of approved API access</li>
          <li>Intentionally submit queries designed to produce misleading, fabricated, or scientifically
            fraudulent results</li>
        </ul>
      </TermsSection>

      <TermsSection num="4" title="Computational Resource Fair Use">
        Consistent with Section 7 of the Terms of Service, computational resources — including DFT
        calculations, generative model runs, and LLM-powered analyses — are subject to fair-use limits
        determined by your subscription tier.
        <br /><br />
        You agree not to:
        <ul style={{ paddingLeft: 20, margin: '8px 0 0 0' }}>
          <li>Submit jobs designed to exhaust shared computational resources</li>
          <li>Queue excessive numbers of concurrent jobs beyond your plan limits</li>
          <li>Use automated scripts to monopolize computational capacity</li>
        </ul>
        <br />
        CrimsonScan reserves the right to queue, throttle, deprioritize, or decline jobs that exceed
        fair-use thresholds. Enterprise customers using Bring Your Own Compute (BYOC) are responsible
        for managing resource consumption on their own infrastructure.
      </TermsSection>

      <TermsSection num="5" title="Account Responsibility">
        You are responsible for all activity conducted under your account. You must maintain the
        confidentiality of your login credentials and must not share them with unauthorized individuals.
        <br /><br />
        If you become aware of any unauthorized use of your account, you must notify CrimsonScan
        immediately at <span style={{ color: '#38bdf8' }}>legal@crimsonscan.com</span>.
        <br /><br />
        Organization administrators are responsible for managing user access within their workspace
        and ensuring that all members comply with this AUP.
      </TermsSection>

      <TermsSection num="6" title="Data Integrity">
        You agree not to knowingly submit false, misleading, or corrupted data to the platform.
        <br /><br />
        You acknowledge that computational results — including DFT calculations, stability predictions,
        property estimates, and AI-generated reports — are provided for research and informational
        purposes. They do not constitute professional scientific advice and must be independently
        validated before use in any safety-critical, regulatory, or commercial application, as
        described in Section 8 of the Terms of Service.
      </TermsSection>

      <TermsSection num="7" title="Intellectual Property Compliance">
        You agree not to use the Service in a manner that infringes the intellectual property rights
        of any third party. You represent that you have the necessary rights to any data you upload
        to the platform.
        <br /><br />
        You acknowledge that the generative models integrated into Spectra (MatterGen, IBM FM4M, GT4SD)
        are subject to their respective open-source licenses. Your use of outputs from these models
        must comply with the applicable license terms.
      </TermsSection>

      <TermsSection num="8" title="Enforcement and Consequences">
        CrimsonScan may investigate suspected violations of this AUP. If we determine that a violation
        has occurred, we may take one or more of the following actions at our sole discretion:
        <ul style={{ paddingLeft: 20, margin: '8px 0 0 0' }}>
          <li>Issue a written warning</li>
          <li>Temporarily suspend access to the Service</li>
          <li>Permanently terminate your account</li>
          <li>Remove or restrict access to content that violates this AUP</li>
          <li>Report the violation to law enforcement authorities if required by law</li>
        </ul>
        <br />
        We will endeavor to provide notice before taking enforcement action, except where immediate
        action is necessary to protect the platform, other users, or comply with legal obligations.
      </TermsSection>

      <TermsSection num="9" title="Reporting Violations">
        If you become aware of any use of the Service that violates this AUP, please report it
        to <span style={{ color: '#38bdf8' }}>legal@crimsonscan.com</span> with the subject
        line "AUP Violation Report." Include as much detail as possible, including dates, descriptions,
        and any relevant evidence.
        <br /><br />
        We will investigate all reports in good faith. Reports may be submitted anonymously.
      </TermsSection>

      <TermsSection num="10" title="Contact">
        For questions about this Acceptable Use Policy, contact us at{' '}
        <span style={{ color: '#38bdf8' }}>legal@crimsonscan.com</span>.
      </TermsSection>
    </div>
  );
}
